import type { EdgeType, Node, Point, Size } from '../types';
import { FONT_FAMILY } from '../constants/typography';
import { isMarkupType } from '../constants/nodeTypes';
import { measureLabelSize } from './measureLabel';

export { isMarkupType };

const MIN_SPAN = 16;

export interface ArrowGeometry {
  position: Point;
  size: Size;
  from: Point;
  to: Point;
}

export function arrowFromPoints(start: Point, end: Point): ArrowGeometry {
  const rawW = Math.abs(end.x - start.x);
  const rawH = Math.abs(end.y - start.y);
  const width = Math.max(rawW, MIN_SPAN);
  const height = Math.max(rawH, MIN_SPAN);
  const minX = Math.min(start.x, end.x) - (width - rawW) / 2;
  const minY = Math.min(start.y, end.y) - (height - rawH) / 2;
  return {
    position: { x: minX, y: minY },
    size: { width, height },
    from: { x: (start.x - minX) / width, y: (start.y - minY) / height },
    to: { x: (end.x - minX) / width, y: (end.y - minY) / height },
  };
}

function fractionPoint(node: Pick<Node, 'data'>, key: 'from' | 'to', fallback: Point): Point {
  const value = node.data?.[key];
  if (value && typeof value === 'object' && 'x' in value && 'y' in value) {
    return value as Point;
  }
  return fallback;
}

export function localArrowPoints(node: Pick<Node, 'size' | 'data'>): { start: Point; end: Point } {
  const from = fractionPoint(node, 'from', { x: 0, y: 0.5 });
  const to = fractionPoint(node, 'to', { x: 1, y: 0.5 });
  return {
    start: { x: from.x * node.size.width, y: from.y * node.size.height },
    end: { x: to.x * node.size.width, y: to.y * node.size.height },
  };
}

export function fitTextNodeSize(text: string, minWidth = 48): Size {
  const measured = measureLabelSize(text || 'Text', `16px ${FONT_FAMILY}`, 22);
  return {
    width: Math.max(minWidth, Math.ceil(measured.width) + 12),
    height: Math.max(24, Math.ceil(measured.height) + 8),
  };
}

export function markupEdgeType(node: Pick<Node, 'type' | 'data'>): EdgeType {
  if (node.type === 'line') return node.data?.edgeType ?? 'no-arrow';
  return node.data?.edgeType ?? 'arrow';
}

export function isStrokeMarkup(type: string): boolean {
  return type === 'arrow' || type === 'line';
}
